import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

/**
 * Validation schema for rejection reason
 * Backend rule: 10-500 characters
 */
const rejectSchema = yup.object({
  reason: yup
    .string()
    .required('Powód odrzucenia jest wymagany')
    .min(10, 'Minimum 10 znaków')
    .max(500, 'Maksimum 500 znaków')
    .trim(),
});

type RejectFormData = yup.InferType<typeof rejectSchema>;

interface RejectImportModalProps {
  open: boolean;
  onClose: () => void;
  fileName: string | null;
  onReject: (reason: string) => void;
  isLoading?: boolean;
}

/**
 * Modal for rejecting invoice import with mandatory reason
 * Implements Phase 4 Manual Review workflow
 * Reference: docs/invoice-import/INVOICE_IMPORT_PHASE_4_BACKEND_RECOMMENDATIONS.md
 */
const RejectImportModal: React.FC<RejectImportModalProps> = ({
  open,
  onClose,
  fileName,
  onReject,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RejectFormData>({
    resolver: yupResolver(rejectSchema),
    defaultValues: {
      reason: '',
    },
  });

  const onSubmit = (data: RejectFormData) => {
    onReject(data.reason.trim());
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>
        <Typography variant="h6">Odrzuć import</Typography>
        <Typography variant="body2" color="text.secondary">
          Plik: {fileName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Import zostanie oznaczony jako odrzucony. Musisz podać powód odrzucenia (10-500 znaków).
        </Alert>

        <form
          id="reject-import-form"
          onSubmit={(event) => {
            // Defensive guard: prevents this Dialog's form submit from bubbling into
            // any future outer <form> ancestor in the React tree (see BatchInvoiceConfigModal).
            event.stopPropagation();
            return handleSubmit(onSubmit)(event);
          }}
        >
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Powód odrzucenia *"
                fullWidth
                multiline
                rows={4}
                error={!!errors.reason}
                helperText={errors.reason?.message || `${field.value.length}/500 znaków (min. 10)`}
                placeholder="Np. Nieprawidłowa kwota brutto, brak wymaganych danych kontrahenta, nieczytelny dokument..."
              />
            )}
          />
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          color="error"
          type="submit"
          form="reject-import-form"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? 'Odrzucanie...' : 'Odrzuć import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectImportModal;
